import { useMemo } from "react";
import { useStudents } from "@/hooks/useStudents";
import { BRAND_COLORS } from "@/lib/brands";
import { ALL_BRANDS, Brand } from "@/types";
import { kstDay } from "@/lib/kst";

/**
 * 상차(적재) 리스트.
 * 브랜드별 × (구/학교) 별 총 박스 수를 집계 — 창고에서 물량 준비, 차량 적재 순서 결정에 사용.
 */
export default function LoadingLists() {
  const { students, loading } = useStudents({ status: "active" });

  const totals = useMemo(() => {
    const byBrand: Record<string, number> = {};
    const byBrandGu: Record<string, Record<string, number>> = {};
    const byBrandSchool: Record<string, Record<string, number>> = {};
    const proxyByCompany: Record<string, { brand: string; count: number }[]> = {};
    let selfTotal = 0, proxyTotal = 0;
    for (const s of students) {
      if (s.courierFlag) continue; // 택배건은 상차 제외
      const b = s.brand;
      const boxes = s.boxesPerMonth || 1;
      byBrand[b] = (byBrand[b] || 0) + boxes;
      byBrandGu[b] = byBrandGu[b] || {};
      byBrandSchool[b] = byBrandSchool[b] || {};
      byBrandGu[b][s.gu || "미상"] = (byBrandGu[b][s.gu || "미상"] || 0) + boxes;
      byBrandSchool[b][s.school || "미상"] = (byBrandSchool[b][s.school || "미상"] || 0) + boxes;

      if (s.isProxy) {
        proxyTotal += boxes;
        const key = s.proxyFor || "기타 대행";
        if (!proxyByCompany[key]) proxyByCompany[key] = [];
        const existing = proxyByCompany[key].find((x) => x.brand === b);
        if (existing) existing.count += boxes;
        else proxyByCompany[key].push({ brand: b, count: boxes });
      } else {
        selfTotal += boxes;
      }
    }
    return { byBrand, byBrandGu, byBrandSchool, proxyByCompany, selfTotal, proxyTotal };
  }, [students]);

  const toCSV = () => {
    let csv = "브랜드,대상,수량(박스)\n";
    for (const b of ALL_BRANDS) {
      csv += `${b},총계,${totals.byBrand[b] || 0}\n`;
      for (const [gu, n] of Object.entries(totals.byBrandGu[b] || {})) {
        csv += `${b},${gu},${n}\n`;
      }
    }
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }); // 엑셀 한글 깨짐 방지
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `상차리스트_${kstDay()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div>로딩 중…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">📦 상차 리스트</h1>
        <button className="btn-secondary" onClick={toCSV}>상차표 파일(CSV) 받기</button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card p-4 border-l-4 border-milk-500">
          <div className="label">자사 배송 박스</div>
          <div className="text-3xl font-extrabold text-milk-700 mt-1">{totals.selfTotal.toLocaleString()}</div>
        </div>
        <div className="card p-4 border-l-4 border-amber-400 bg-amber-50">
          <div className="label">🚛 타사 대행 박스</div>
          <div className="text-3xl font-extrabold text-amber-700 mt-1">{totals.proxyTotal.toLocaleString()}</div>
        </div>
      </section>

      {totals.proxyTotal > 0 && (
        <section className="card p-4 bg-amber-50 border border-amber-200">
          <h3 className="font-bold mb-2 text-amber-800">🚛 대행 의뢰회사별 상차 수량</h3>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600 border-b border-amber-200">
                <th className="p-2">의뢰 회사</th>
                <th className="p-2">우유 브랜드</th>
                <th className="p-2 text-right">박스 수</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(totals.proxyByCompany).map(([co, arr]) =>
                arr.map((x, i) => (
                  <tr key={`${co}-${x.brand}`} className="border-t border-amber-100">
                    {i === 0 && <td className="p-2 font-bold" rowSpan={arr.length}>{co}</td>}
                    <td className="p-2">{x.brand}</td>
                    <td className="p-2 text-right font-semibold">{x.count}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </section>
      )}

      <section className="card p-4">
        <h3 className="font-bold mb-2">브랜드별 총 박스 수</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          {ALL_BRANDS.map((b) => {
            const n = totals.byBrand[b] || 0;
            if (!n) return null;
            return (
              <div key={b} className={`rounded-lg p-3 ${BRAND_COLORS[b as Brand]}`}>
                <div className="text-xs opacity-70">{b}</div>
                <div className="text-2xl font-extrabold">{n.toLocaleString()}박스</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card p-4">
        <h3 className="font-bold mb-2">브랜드 × 구별 상차 수</h3>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left">
                <th className="p-2">브랜드</th>
                <th className="p-2">구</th>
                <th className="p-2 text-right">박스</th>
              </tr>
            </thead>
            <tbody>
              {ALL_BRANDS.map((b) =>
                Object.entries(totals.byBrandGu[b] || {}).map(([gu, n]) => (
                  <tr key={`${b}-${gu}`} className="border-t">
                    <td className="p-2">
                      <span className={`chip ${BRAND_COLORS[b as Brand]}`}>{b}</span>
                    </td>
                    <td className="p-2">{gu}</td>
                    <td className="p-2 text-right font-semibold">{n}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-4">
        <h3 className="font-bold mb-2">브랜드 × 학교별 상차 수 (하차지 라벨링용)</h3>
        <div className="overflow-auto max-h-[50vh]">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-2">브랜드</th>
                <th className="p-2">학교</th>
                <th className="p-2 text-right">박스</th>
              </tr>
            </thead>
            <tbody>
              {ALL_BRANDS.map((b) =>
                Object.entries(totals.byBrandSchool[b] || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([sch, n]) => (
                    <tr key={`${b}-${sch}`} className="border-t">
                      <td className="p-2">
                        <span className={`chip ${BRAND_COLORS[b as Brand]}`}>{b}</span>
                      </td>
                      <td className="p-2">{sch}</td>
                      <td className="p-2 text-right font-semibold">{n}</td>
                    </tr>
                  )),
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
